<template>
  <div class="subscription-expiring-bar">
    <template v-if="promisedActive">
      <span class="subscription-expiring-bar__term">
        Обещанный платёж действует <b>{{ promisedEndsText }}</b>.
      </span>
      <span>
        Пополните баланс до этого срока, иначе работа с заказами остановится.
      </span>
    </template>
    <template v-else-if="promisedPending">
      <span class="subscription-expiring-bar__term">
        Баланса хватит <b>{{ runsOutText }}</b>,
      </span>
      <span>
        затем на {{ promisedTermText }} включится обещанный платёж.
      </span>
    </template>
    <template v-else-if="inDebt || runsOutToday">
      <span class="subscription-expiring-bar__term">
        Баланс исчерпан.
      </span>
      <span>
        Пополните его сейчас, иначе работа с заказами скоро остановится.
      </span>
    </template>
    <template v-else>
      <span class="subscription-expiring-bar__term">
        Баланса хватит <b>{{ runsOutText }}</b>.
      </span>
      <span>
        После этого работа с заказами остановится — пополните баланс или подключите автоплатёж.
      </span>
    </template>
  </div>
</template>

<script>
  import { mapState } from 'vuex'
  import { DateTime } from 'luxon'
  import { numWord } from '@/util/string'

  // Бэк отдаёт момент ночного списания (01:00 МСК), на котором счёт блокируется.
  // Последний рабочий день — накануне, в поясе браузера: в Хабаровске списание
  // в 08:00, и день, начатый до него, уже не обещаем.
  function blocksAtMoment (iso) {
    const moment = iso ? DateTime.fromISO(iso).setLocale('ru') : null

    return moment && moment.isValid ? moment : null
  }

  function untilText (iso) {
    const blocksAt = blocksAtMoment(iso)

    if (!blocksAt) {
      return ''
    }

    const today = DateTime.local().startOf('day')
    const lastDay = DateTime.max(blocksAt.minus({ days: 1 }).startOf('day'), today)

    if (lastDay.hasSame(today, 'day')) {
      return 'до конца дня'
    }

    if (lastDay.hasSame(today.plus({ days: 1 }), 'day')) {
      return 'до конца завтрашнего дня'
    }

    return `до конца ${lastDay.toFormat('d MMMM')}`
  }

  // Блокирующее списание уже сегодня: в Хабаровске это утро до 08:00
  function isToday (iso) {
    const blocksAt = blocksAtMoment(iso)

    return !blocksAt || blocksAt.hasSame(DateTime.local(), 'day')
  }

  export default {
    name: 'SubscriptionExpiringBar',
    props: {
      // Отсрочка включена заранее: блокировки после даты истечения не будет,
      // вместо призыва пополнить «сейчас» объясняем, что произойдёт
      promisedPending: {
        type: Boolean,
        default: false,
      },
    },
    computed: {
      ...mapState('billing', ['paymentAccount']),
      runsOutText () {
        return untilText(this.paymentAccount.runsOutAt)
      },
      runsOutToday () {
        return isToday(this.paymentAccount.runsOutAt)
      },
      // Оплаченной даты нет только при долге: счёт живёт до ближайшего списания
      inDebt () {
        return !this.paymentAccount.validUntil
      },
      promisedActive () {
        return !!this.paymentAccount.promisedPayment?.active
      },
      promisedEndsText () {
        return untilText(this.paymentAccount.promisedPayment?.endsAt)
      },
      promisedTermText () {
        const days = this.paymentAccount.promisedPayment?.termDays ?? 0

        return `${days} ${numWord(days, 'день', 'дня', 'дней')}`
      },
    },
  }
</script>

<style scoped lang="less">
// Внешний вид и место полосы задаёт TopBar, здесь только раскладка текста
.subscription-expiring-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;

  b {
    font-weight: 700;
  }

  // Дату отрыва от срока не допускаем: они читаются вместе.
  // Отступ margin'ом, а не gap: flex gap не знают Safari до 14.1 и IE
  &__term {
    margin-right: 4px;
    white-space: nowrap;
  }

  // Влево, как сообщения о связи в той же полосе: в несколько строк текст по
  // центру читается хуже
  .media-sm({
    width: 100%;
    justify-content: flex-start;
    text-align: left;
  });

  .media-xs({
    // На узком экране срок и дата уже не помещаются в строку с датой целиком
    &__term {
      white-space: normal;
    }
  });
}
</style>
